import React, { useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import ApiClient from '../../utils/ApiClient';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export default function MarketplaceProsBattle({ data }) {
    const [trendData, setTrendData] = useState(null);
    const [trendAsin, setTrendAsin] = useState(null);
    const [trendLoading, setTrendLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const handleRowClick = async (asin) => {
        setTrendAsin(asin);
        setTrendLoading(true);
        setShowModal(true);
        try {
            const res = await ApiClient.get(`/api/business-insights/mp-trend?asin=${asin}&days=30`);
            setTrendData(res.data);
        } catch (err) {
            console.error('Error fetching trend data', err);
            setTrendData([]);
        } finally {
            setTrendLoading(false);
        }
    };

    if (!data || data.length === 0) {
        return <p className="text-gray-500 py-4">No shared ASINs with Marketplace Pros found in the last 30 days.</p>;
    }

    const chartData = trendData && trendData.length > 0 ? {
        labels: trendData.map(t => t.reportDate),
        datasets: [
            {
                label: 'Us (BB Win %)',
                data: trendData.map(t => t.ourBbWinPct),
                borderColor: 'rgb(34, 197, 94)',
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                tension: 0.3,
            },
            {
                label: 'Marketplace Pros (BB Win %)',
                data: trendData.map(t => t.mpBbWinPct),
                borderColor: 'rgb(239, 68, 68)',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                tension: 0.3,
            },
        ],
    } : null;

    return (
        <>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                            <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Our Price</th>
                            <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">MP Price</th>
                            <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gap</th>
                            <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Our BB%</th>
                            <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">MP BB%</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Winner</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {data.map((item, idx) => (
                            <tr
                                key={idx}
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleRowClick(item.asin)}
                            >
                                <td className="px-3 py-3 text-sm">
                                    <div className="font-medium text-gray-900 truncate max-w-[200px]" title={item.productName}>
                                        {item.productName}
                                    </div>
                                    <div className="text-xs text-gray-400">{item.asin}</div>
                                </td>
                                <td className="px-3 py-3 text-sm text-right">
                                    {item.ourPrice != null ? `$${Number(item.ourPrice).toFixed(2)}` : '-'}
                                </td>
                                <td className="px-3 py-3 text-sm text-right">
                                    {item.mpPrice != null ? `$${Number(item.mpPrice).toFixed(2)}` : '-'}
                                </td>
                                <td className={`px-3 py-3 text-sm text-right font-medium ${item.priceGap > 0 ? 'text-red-600' : item.priceGap < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                                    {item.priceGap != null ? `$${Number(item.priceGap).toFixed(2)}` : '-'}
                                </td>
                                <td className={`px-3 py-3 text-sm text-right font-medium ${item.ourBbWinPct > item.mpBbWinPct ? 'text-green-600' : 'text-gray-600'}`}>
                                    {item.ourBbWinPct}%
                                </td>
                                <td className={`px-3 py-3 text-sm text-right font-medium ${item.mpBbWinPct > item.ourBbWinPct ? 'text-red-600' : 'text-gray-600'}`}>
                                    {item.mpBbWinPct}%
                                </td>
                                <td className="px-3 py-3 text-sm">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        item.currentWinner === 'US' ? 'bg-green-100 text-green-800' :
                                        item.currentWinner === 'MP' ? 'bg-red-100 text-red-800' :
                                        'bg-gray-100 text-gray-600'
                                    }`}>
                                        {item.currentWinner}
                                    </span>
                                </td>
                                <td className="px-3 py-3 text-sm text-gray-600">{item.suggestedAction}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <p className="text-xs text-gray-400 mt-2">Click a row to see Buy Box trend over time</p>
            </div>

            {showModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} />
                        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-medium text-gray-900">
                                    Buy Box Trend: {trendAsin}
                                </h3>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                                >
                                    &times;
                                </button>
                            </div>
                            {trendLoading ? (
                                <div className="flex justify-center py-12">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                                </div>
                            ) : chartData ? (
                                <Line
                                    data={chartData}
                                    options={{
                                        responsive: true,
                                        scales: {
                                            y: {
                                                beginAtZero: true,
                                                max: 100,
                                                title: { display: true, text: 'BB Win %' },
                                            },
                                        },
                                        plugins: {
                                            legend: { position: 'top' },
                                        },
                                    }}
                                />
                            ) : (
                                <p className="text-gray-500 text-center py-8">No trend data available.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
